import type { DocumentNode, TypedDocumentNode } from '@apollo/client/core';
import type { QueryHookOptions } from '@apollo/client/react/types/types';
import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

interface PageVariables {
    from: number;
    size: number;
}

const usePaginatedQuery = <TPageVariables extends object = PageVariables>(
    query: DocumentNode | TypedDocumentNode,
    options: QueryHookOptions & {
        initialPageVariables: TPageVariables;
        getNextPageVariables: (lastPage: any, lastPageVariables: TPageVariables) => TPageVariables | null;
    }
) => {
    const { initialPageVariables, getNextPageVariables, variables = {}, ...queryOptions } = options || {};
    const { data, loading, error, fetchMore } = useQuery(query, {
        ...queryOptions,
        variables: { ...variables, ...initialPageVariables },
    });

    const [nextPages, setNextPages] = useState([]);
    const [nextPageVariables, setNextPageVariables] = useState([]);
    const [isNextPageLoading, setIsNextPageLoading] = useState(false);

    const pages = useMemo(() => {
        if (!data) return [];
        return [data, ...nextPages];
    }, [data, nextPages]);

    const pageVariables = useMemo(() => {
        if (pages.length === 0) return [];
        return [initialPageVariables, ...nextPageVariables];
    }, [pages, initialPageVariables, nextPageVariables]);

    const hasNextPage = useMemo(
        () => getNextPageVariables(pages[pages.length - 1], pageVariables[pageVariables.length - 1]) !== null,
        [pages, pageVariables, getNextPageVariables]
    );

    useEffect(() => {
        if (!loading) return;
        // Reset pagination when variables change
        setNextPages([]);
        setNextPageVariables([]);
    }, [loading]);

    const loadNextPage = async () => {
        if (!hasNextPage) return;

        const nextPageVariables = getNextPageVariables(
            pages[pages.length - 1],
            pageVariables[pageVariables.length - 1]
        );

        setIsNextPageLoading(true);
        try {
            const nextPage = await fetchMore({ variables: { ...variables, ...nextPageVariables } });

            setNextPages((prevPages) => [...prevPages, nextPage.data]);
            setNextPageVariables((prevVars) => [...prevVars, nextPageVariables]);
        } finally {
            setIsNextPageLoading(false);
        }
    };

    return { pages, loading, error, hasNextPage, loadNextPage, nextPageLoading: isNextPageLoading };
};

export default usePaginatedQuery;
